import {
  useFlatfile,
  FlatfileProvider,
  Workbook,
  useEvent,
} from "@flatfile/react";
import api from "@flatfile/api";
import { isProd } from "../../../../env";
import { stripeWorkbookConfig } from "../../FlatFileConfigs/stripeWorkbookConfig";
import {
  FLATFILE_PUBLISHABLE_KEY_PROD,
  FLATFILE_PUBLISHABLE_KEY_DEV,
} from "../../constants";
import { fetchSheetRecords } from "../../api/migrationAssistantApi";
import { flatFileRecordModifiers } from "../helpers";
import { usePostStripeCustomerData } from "../../api/migrationAssistantApi.hooks";
import { Button } from "@pushpress/pushpress-ui";
import { Upload } from "lucide-react";
import LoadingState from "../../components/shared/LoadingState";
const FlatfilePortal = ({
  setShowWaitingState,
}: {
  setShowWaitingState: (show: boolean) => void;
  isLoading: boolean;
}) => {
  const { openPortal, closePortal } = useFlatfile();
  const { mutate: postStripeCustomerData } = usePostStripeCustomerData();

  useEvent("job:ready", { job: "workbook:submitActionFg" }, async (event) => {
    const {
      context: { jobId, workbookId },
    } = event;

    try {
      const { data: sheets } = await api.sheets.list({ workbookId });
      const records = await fetchSheetRecords(sheets);

      if (!records) {
        await api.jobs.fail(jobId, {
          outcome: {
            message: "There was an error fetching records from flatfile",
          },
        });
        return;
      }

      postStripeCustomerData(
        {
          customerIds: records.map(
            (record) => record.values.stripeCustomerId?.value as string,
          ),
        },
        {
          onSuccess: async (response) => {
            await api.jobs.complete(jobId, {
              outcome: {
                message: `Processing stripe data for ${response.allJobs} members`,
              },
            });
            closePortal();

            if (response.allJobs !== 0) {
              setShowWaitingState(true);
            }
          },
          onError: async (error) => {
            await api.jobs.fail(jobId, {
              outcome: {
                message: JSON.stringify((error as Error).message),
              },
            });
          },
        },
      );
    } catch (error: unknown) {
      await api.jobs.fail(jobId, {
        outcome: {
          message: JSON.stringify(error),
        },
      });
    }
  });

  return (
    <>
      <Button onClick={openPortal} className="tw-w-full">
        <Upload className="tw-w-4 tw-h-4 tw-mr-2 tw-flex-shrink-0" />
        Upload mapping file
      </Button>
      <Workbook
        config={stripeWorkbookConfig}
        onRecordHooks={[
          ["stripeDataSheet", (record) => flatFileRecordModifiers(record)],
        ]}
      />
    </>
  );
};

export default function FlatFileUploader({
  setShowWaitingState,
  isLoading,
}: {
  setShowWaitingState: (show: boolean) => void;
  isLoading: boolean;
}) {
  const PUBLISHABLE_KEY = isProd()
    ? FLATFILE_PUBLISHABLE_KEY_PROD
    : FLATFILE_PUBLISHABLE_KEY_DEV;

  if (isLoading) {
    return <LoadingState width={288} height={38} />;
  }

  return (
    <FlatfileProvider publishableKey={PUBLISHABLE_KEY}>
      <FlatfilePortal
        setShowWaitingState={setShowWaitingState}
        isLoading={isLoading}
      />
    </FlatfileProvider>
  );
}
